import { errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { marketingHead } from "@/lib/marketing/seo";
import { breadcrumbs, webPageOf } from "@/lib/marketing/ppl-schema";
import { useState } from "react";
import { MLink } from "@/lib/marketing/m-link";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Reveal } from "@/components/marketing/reveal";
import { orpcPublicClient } from "@/lib/orpc";
import { readAttribution } from "@/lib/marketing/attribution";
import { toast } from "sonner";
import { MarketingShell } from "@/components/marketing/site/marketing-shell";
import { HoneypotField } from "@/components/marketing/honeypot-field";
import { IntakeHero } from "@/components/marketing/site/intake-hero";
import { IntakeNameFields } from "@/components/marketing/site/intake-name-fields";
import { IntakePhoneField } from "@/components/marketing/site/intake-phone-field";
import { IntakeReasons } from "@/components/marketing/site/intake-reasons";
import { HeartHandshake, Clock, ShieldCheck, ArrowRight, CheckCircle2 } from "@/lib/public-icons";

const reasons = [
  {
    icon: HeartHandshake,
    title: "Tell us about your loved one",
    description: "Share what care you're looking for and we'll guide you to the right fit.",
  },
  {
    icon: Clock,
    title: "A real person will follow up",
    description: "Your inquiry goes straight to the community team, usually answered within a day.",
  },
  {
    icon: ShieldCheck,
    title: "Your details stay private",
    description: "We only use what you share to help with your inquiry. No spam, ever.",
  },
];

function MarketingInquire() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [relationship, setRelationship] = useState("self");
  const [__hp, setHp] = useState(""); // honeypot (intentionally non-human field name)
  const [source] = useState(() => readAttribution("inquire"));
  const [sent, setSent] = useState(false);

  const mutation = useMutation({
    mutationFn: async () => {
      await orpcPublicClient.crm.websiteIntake.inquiry({
        firstName,
        lastName,
        email,
        phone,
        message,
        relationship,
        source,
        __hp,
      });
    },
    onSuccess: () => setSent(true),
    onError: (err: Error) => {
      toast.error("We couldn't send your inquiry", {
        description: errorWords(err, "Please try again in a moment."),
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate();
  };

  return (
    <MarketingShell>
      <IntakeHero
        id="inquire"
        badge="Request information"
        title={
          <>
            Find the right <span className="text-gradient-warm">community</span> for your family
          </>
        }
        lead="Share a few details about your loved one's needs and a community team member will reach out to help, no pressure, no obligation."
      >
        <div className="mx-auto mt-14 grid max-w-5xl items-start gap-8 md:grid-cols-[1fr_1.1fr]">
          <Reveal>
            <IntakeReasons items={reasons} />
          </Reveal>

          {/* Form */}
          <Reveal delay={80}>
            <Card className="p-6 md:p-8">
              {sent ? (
                <div
                  className="flex flex-col items-center py-8 text-center"
                  data-testid="inquire-success"
                >
                  <span className="inline-flex size-14 items-center justify-center rounded-2xl bg-primary/15 text-primary">
                    <CheckCircle2 className="size-7" />
                  </span>
                  <h2 className="font-display mt-5 text-2xl font-semibold tracking-tight">
                    Thank you, we've got your inquiry
                  </h2>
                  <p className="mt-3 max-w-sm text-sm text-muted-foreground">
                    A community team member will reach out soon using the contact details you
                    shared. We're glad you're here.
                  </p>
                  <div className="mt-7 flex flex-wrap justify-center gap-3">
                    <Button className="font-ui" data-testid="button-inquire-home" asChild>
                      <MLink href="/">
                        Back to home
                        <ArrowRight className="ml-2 size-4" />
                      </MLink>
                    </Button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <HoneypotField id="inquire-hp" value={__hp} onChange={setHp} />
                  <IntakeNameFields
                    idPrefix="inquire"
                    firstName={firstName}
                    lastName={lastName}
                    onFirstName={setFirstName}
                    onLastName={setLastName}
                  />
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="inquire-email">Email (optional)</Label>
                      <Input
                        id="inquire-email"
                        type="email"
                        maxLength={200}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="e.g. jane@example.com"
                        data-testid="input-email"
                      />
                    </div>
                    <IntakePhoneField id="inquire-phone" value={phone} onChange={setPhone} />
                  </div>
                  <p className="-mt-2 text-xs text-muted-foreground">
                    A community team member calls you back first. Email is optional.
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="inquire-relationship">Who are you inquiring for?</Label>
                    <Select value={relationship} onValueChange={setRelationship}>
                      <SelectTrigger id="inquire-relationship" data-testid="select-relationship">
                        <SelectValue placeholder="Select relationship" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="self">Myself</SelectItem>
                        <SelectItem value="child">Parent (I'm their child)</SelectItem>
                        <SelectItem value="spouse">Spouse / Partner</SelectItem>
                        <SelectItem value="sibling">Sibling</SelectItem>
                        <SelectItem value="relative">Other relative</SelectItem>
                        <SelectItem value="friend">Friend</SelectItem>
                        <SelectItem value="social_worker">Social worker / Case manager</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="inquire-message">How can we help?</Label>
                    <Textarea
                      id="inquire-message"
                      rows={5}
                      maxLength={2000}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Tell us a little about your loved one's needs and timing…"
                      data-testid="input-message"
                    />
                  </div>
                  <Button
                    type="submit"
                    size="lg"
                    className="w-full font-ui"
                    disabled={mutation.isPending}
                    data-testid="button-submit-inquiry"
                  >
                    {mutation.isPending ? "Sending…" : "Send my inquiry"}
                    {!mutation.isPending && <ArrowRight className="ml-2 size-4" />}
                  </Button>
                  <p className="text-center text-xs text-muted-foreground">
                    Run a community yourself?{" "}
                    <MLink href="/demo">
                      <span className="cursor-pointer text-primary" data-testid="link-demo-inline">
                        Book a demo
                      </span>
                    </MLink>
                    .
                  </p>
                </form>
              )}
            </Card>
          </Reveal>
        </div>
      </IntakeHero>
    </MarketingShell>
  );
}

export const Route = createFileRoute("/v2/inquire")({
  component: MarketingInquire,
  head: () => {
    const seo = {
      title: "Request information: find the right community for your family | PEOPLE AI Workforce",
      description:
        "Share a few details about your loved one's needs and a community team member will reach out to help. No pressure, no obligation, and your details stay private.",
      path: "/v2/inquire",
      imageAlt: "PEOPLE AI Workforce. The operating system for assisted living communities",
    };
    return marketingHead({
      ...seo,
      schema: [webPageOf(seo), breadcrumbs({ name: "Request information", path: "/inquire" })],
    });
  },
});
